import { Injectable } from "@angular/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { LocalNotifications } from "@capacitor/local-notifications";
import { HttpClient } from "@angular/common/http";
import { UserAuthService } from "./user-auth.service";
import { connLink } from "src/global";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { PushNotificationType } from "node_js/models/enums/push-notification-type";

@Injectable({
  providedIn: "root",
})
export class PushNotificationService {
  private messageCache: { [componentId: string]: string[] } = {};
  isAppActive = false;
  constructor(
    private http: HttpClient,
  ) {
    this.initForegroundPushHandlers();

    App.addListener('appStateChange', ({ isActive }) => {
        this.isAppActive = isActive;
    });
  }

  /**
   * 🔔 Handle foreground push messages
   */
  private initForegroundPushHandlers() {
    const platform = Capacitor.getPlatform();
    
    if (Capacitor.isNativePlatform()) {  
      PushNotifications.addListener("pushNotificationReceived", (notification) => {
        const { type } = notification.data || {};
        if (type === PushNotificationType.COMPONENT_VALUE_UPDATE) {
          this.handleComponentValueChangePush(notification.data);
        } 
      });
    } else {
      console.log(`🌐 [PushService] Platform ${platform} detected, skipping foreground push handlers`);
    }
    
  }

  /**
   * 💬 Handle chat message push
   */
  handleComponentValueChangePush(data: any) {
    const { componentId, senderName, message } = data;
  
    if (!componentId || !message) return;
  
    if (!this.messageCache[componentId]) {
      this.messageCache[componentId] = [];
    }
  
    this.messageCache[componentId].push(`${senderName}: ${message}`);
  
    this.showStackedComponentValueChangesNotification(componentId, this.messageCache[componentId]);
  }
  
  async getPushToken(askPermissionIfPrompt: boolean, userAuthService: UserAuthService): Promise<string | null> {
    try {
      const platform = Capacitor.getPlatform();
      
      // Return early for web platform
      if (platform === 'web') {
        console.log('🌐 [PushService] Web platform detected, returning null for push token');
        return null;
      }
      
      const permissionGranted = await this.ensurePushPermission(askPermissionIfPrompt);
      if (!permissionGranted) return null;  
  
      if (platform === 'ios') {
        return await this.getIosPushToken(userAuthService);
      }
  
      return await this.getAndroidPushToken();
    } catch (error) {
      console.error('Unexpected error getting push token:', error);
      return null;
    }
  }

  async ensurePushPermission(askPermission: boolean): Promise<boolean> {
    const platform = Capacitor.getPlatform();
    
    // Return true for web platform (no permissions needed)
    if (platform === 'web') {
      console.log('🌐 [PushService] Web platform detected, no push permissions needed');
      return true;
    }
    
    const permStatus = await PushNotifications.checkPermissions();
    console.log('Push permission status:', permStatus.receive);
  
    if (permStatus.receive === 'denied') return false;
  
    if (permStatus.receive === 'prompt') {
      if (!askPermission) return false;
  
      const requestStatus = await PushNotifications.requestPermissions();
      console.log('Push permission requested:', requestStatus.receive);
      return requestStatus.receive === 'granted';
    }
  
    return true;
  }

  async getIosPushToken(userAuthService: UserAuthService): Promise<string | null> {
    const maxAttempts = 10;
    const delayMs = 1000;
  
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`📡 [iOS] Attempt ${attempt} to get FCM token`);
  
        // ✅ Prefer native injected token if available
        if (typeof window !== 'undefined' && (window as any).nativeFCMToken) {
          console.log('✅ FCM Token from AppDelegate injection');
          const token = (window as any).nativeFCMToken;
          
          // 🔥 CRITICAL: Register token with backend immediately
          if (token && userAuthService?.currentUserComplete?.user?.authUid) {
            console.log('🔥 [iOS] Auto-registering FCM token with backend');
            this.registerPushNotificationUser(token, userAuthService);
          }
          
          return token;
        }
  
        // ✅ Fallback to plugin
        if (typeof window !== 'undefined' && (window as any).Capacitor?.Plugins?.FCMTokenPlugin) {
          const pluginResult = await (window as any).Capacitor.Plugins.FCMTokenPlugin.getFCMToken();
          const token = pluginResult?.token;
          if (token) {
            console.log('✅ FCM Token from iOS plugin:', token);
            
            // 🔥 CRITICAL: Register token with backend immediately
            if (userAuthService?.currentUserComplete?.user?.authUid) {
              console.log('🔥 [iOS] Auto-registering FCM token with backend');
              this.registerPushNotificationUser(token, userAuthService);
            }
            
            return token;
          }
        }
  
      } catch (error) {
        console.warn(`⚠️ [iOS] Error getting token (attempt ${attempt}):`, error);
      }
  
      // Wait before next retry
      await new Promise((res) => setTimeout(res, delayMs));
    }
  
    console.error('❌ Failed to get iOS FCM token after polling');
    return null;
  }
  
  
  
  async getAndroidPushToken(): Promise<string | null> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('⚠️ Android push registration timed out.');
        PushNotifications.removeAllListeners();
        resolve(null);
      }, 7000);
  
      const onRegister = (token: { value: string }) => {
        clearTimeout(timeout);
        console.log('✅ FCM Token (Android):', token.value);
        PushNotifications.removeAllListeners();
        resolve(token.value);
      };
  
      const onError = (error: any) => {
        clearTimeout(timeout);
        console.error('❌ Android push registration error:', error);
        PushNotifications.removeAllListeners();
        resolve(null);
      };
  
      PushNotifications.addListener('registration', onRegister);
      PushNotifications.addListener('registrationError', onError);
  
      console.log('📲 Calling PushNotifications.register() on Android...');
      PushNotifications.register();
    });
  }
  
  /**
   * 📥 Show stacked local notification for chat messages
   */
  async showStackedComponentValueChangesNotification(componentId: string, lines: string[]) {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: this.getNotificationIdFromConversationId(componentId),
          title: 'New Messages',
          body: lines.slice(-3).join('\n'), // last 3 messages stacked
          extra: { componentId },
          channelId: 'messages',
        },
      ],
    });
  }
  

  /**
   * ❌ Clear notification & cache when conversation is opened
   */
  async deleteConversationPushNotifications(conversationId: string) {

  
    if (Capacitor.isNativePlatform()){
      await LocalNotifications.cancel({
        notifications: [
          {
            id: this.getNotificationIdFromConversationId(conversationId),
          },
        ],
      });

      delete this.messageCache[conversationId]; 
    }
    
  }

  /**
   * 📱 Convert conversation ID to notification ID
   */
  private getNotificationIdFromConversationId(conversationId: string): number {
    return parseInt(conversationId, 36) % 100000;
  }

  /**
   * 🚀 Register FCM token with backend
   */
  registerPushNotificationUser(fcmToken: string, userAuthService: UserAuthService): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const idToken = await userAuthService.createIdToken();
        const uid = userAuthService.currentUserComplete.user.authUid;

        console.log('🔥 [PushService] Registering FCM token with backend:', fcmToken);
        console.log('🔥 [PushService] User UID:', uid);

        this.http
          .post(`${connLink}/registerPushToken`, { uid, idToken, fcmToken })
          .subscribe({
            next: (response) => {
              console.log('✅ [PushService] FCM token registered successfully:', response);
              resolve();
            },
            error: (error) => {
              console.error('❌ [PushService] Failed to register FCM token:', error);
              reject();
            },
          });
      } catch (err) {
        console.error('❌ [PushService] Error in registerPushNotificationUser:', err);
        reject();
      }
    });
  }

  /**
   * 🔥 Auto-register iOS FCM token when available
   */
  async registerIosTokenIfAvailable(userAuthService: UserAuthService): Promise<void> {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'ios') {
      const token = await this.getIosPushToken(userAuthService);
      if (token && userAuthService?.currentUserComplete?.user?.authUid) {
        console.log('🔥 [PushService] Auto-registering iOS FCM token:', token);
        await this.registerPushNotificationUser(token, userAuthService);
      }
    } else {
      console.log(`🌐 [PushService] Platform ${platform} detected, skipping iOS token registration`);
    }
  }

  /**
   * 🔥 Wait for iOS FCM token to be injected by native code
   */
  async waitForIosFCMToken(): Promise<string> {
    const platform = Capacitor.getPlatform();
    
    // Return early for non-iOS platforms
    if (platform !== 'ios') {
      console.log(`🌐 [PushService] Platform ${platform} detected, cannot wait for iOS FCM token`);
      throw new Error(`Platform ${platform} is not iOS`);
    }
    
    const maxAttempts = 20; // Wait up to 20 seconds
    const delayMs = 1000;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`🔥 [PushService] iOS FCM token attempt ${attempt}/${maxAttempts}`);
        
        // Check if native token is available
        if (typeof window !== 'undefined' && (window as any).nativeFCMToken) {
          const token = (window as any).nativeFCMToken;
          console.log('✅ [PushService] iOS FCM token found:', token);
          return token;
        }
        
        // Fallback to plugin
        if (typeof window !== 'undefined' && (window as any).Capacitor?.Plugins?.FCMTokenPlugin) {
          try {
            const pluginResult = await (window as any).Capacitor.Plugins.FCMTokenPlugin.getFCMToken();
            const token = pluginResult?.token;
            if (token) {
              console.log('✅ [PushService] iOS FCM token from plugin:', token);
              return token;
            }
          } catch (error) {
            console.log(`⚠️ [PushService] Plugin attempt ${attempt} failed:`, error);
          }
        }
        
        console.log(`⏳ [PushService] Waiting for iOS FCM token (attempt ${attempt})...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
        
      } catch (error) {
        console.warn(`⚠️ [PushService] Error in attempt ${attempt}:`, error);
      }
    }
    
    throw new Error('Failed to get iOS FCM token after waiting');
  }

  /**
   * ⚙️ Request permissions and setup listeners
   */
  async checkPermissions(userAuthService: UserAuthService) {
    try {
      const platform = Capacitor.getPlatform();
      
      // Skip push notification setup for web platform
      if (platform === 'web') {
        console.log('🌐 [PushService] Web platform detected, skipping push notification setup');
        return;
      }

      // Only register once the user is logged in
      PushNotifications.addListener("pushNotificationActionPerformed", (notification) => {
        const convId = notification.notification.data?.conversationId;
        if (convId) {
          console.log("Push tapped for conversation:", convId);
          // Future: route to chat detail
        }
      });

      PushNotifications.addListener("registration", (token) => {
        console.info("FCM Registration token:", token.value);
        userAuthService.currentUserComplete.userDevice.pushToken = token.value;
        userAuthService.updateUserDevice();
      });

      PushNotifications.addListener("registrationError", (err) => {
        console.error("Registration error:", err.error);
      });

      let permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === "prompt") {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive === "granted") {
        await PushNotifications.register();
      } else {
        console.warn("User denied push notification permissions.");
      }
    } catch (err) {
      console.error("Push permission check error:", err);
    }
  }
}
